import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { formatStringLowercaseWithDashes } from 'shared/utils/formatStringLowercaseWithDashes';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';

const TP = 'pages.pageSlug.silhouette.silhouette';

const SilhouettePage = ({
  brandDisplayName,
  brandName,
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  constructorResponse,
  displayName,
  globalPricingId,
  silhouette,
  isCrawler = false,
}: IProductLandingPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = null,
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      brandName={brandName}
      breadcrumbs={[
        { name: startCase(category), slug: `/${category}` },
        {
          name: brandDisplayName,
          slug: `/${category}/brand/${formatStringLowercaseWithDashes(brandName)}`,
        },
        {
          name: startCase(silhouette),
          slug: `/${category}/silhouette/${silhouette}`,
          isStatic: true,
        },
      ]}
      browseKey={browseKey}
      browseValue={browseValue}
      category={category}
      constructorResponse={constructorResponse}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      hasBreadcrumbSchema
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      silhouette={silhouette}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const { pageSlug: category, silhouette }: { pageSlug: string; silhouette: string } = query;
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${BREADCRUMBS.SILHOUETTE}/${silhouette}`,
  });

  const browseName: string = startCase(silhouette);
  const displayName = t(`${TP}.displayName`, '{browseName} Sneakers', { browseName });
  const isCrawler = isSearchCrawler(req?.headers);

  try {
    const browseResponse = await getBrowseResults({
      req,
      res,
      query,
      resolvedUrl,
      filterKey: 'silhouette',
      filterValue: browseName?.toLowerCase(),
    });
    const invalidBrowse = browseResponse.response.total_num_results === 0;

    if (invalidBrowse) {
      return {
        notFound: true,
      };
    }

    const brandFacet = browseResponse?.response?.facets?.filter((facet) => facet?.name === 'brand');
    const brandName = brandFacet?.[0]?.options?.[0]?.value;
    const brandDisplayName: string = brandFacet?.[0]?.options?.[0]?.display_name || '';

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        brandName,
        brandDisplayName,
        breadcrumbs,
        browseKey: 'silhouette',
        browseValue: browseName?.toLowerCase(),
        category,
        constructorResponse: browseResponse,
        displayName,
        globalPricingId,
        silhouette,
        isCrawler,
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default SilhouettePage;
